import { Box } from '@mui/material'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import DataGridSkeleton from '@/components/dataDisplay/DataGridSkeleton'
import RevenueMonthlyDataGrid from '@/features/site/components/revenue/RevenueMonthlyDataGrid'
import { RevenueMonthlySummary } from '@/features/site/components/revenue/RevenueMonthlySummary'
import type { RevenueSummaryAccordionProps } from '@/features/site/components/revenue/RevenueSummaryAccordion'
import RevenueSummaryAccordion from '@/features/site/components/revenue/RevenueSummaryAccordion'
import type { Month } from '@/features/site/constants'
import { MONTHS } from '@/features/site/constants'
import type { RevenueDataRow } from '@/features/site/types/revenue'
import { getRevenueDataByMonth } from '@/features/site/utils/revenueUtils/formatRevenueData'
import { getCurrencyFromRevenueRows } from '@/features/site/utils/revenueUtils/getCurrencyFromRevenues'

type RevenueMonthlyAccordionsProps = {
  revenueData: RevenueDataRow[]
  isLoadingRevenueTimeSeries: boolean
}

const SKELETON_ROW_SIZE = 12
const SKELETON_COLUMN_SIZE = 4

export const RevenueMonthlyAccordions = ({
  revenueData,
  isLoadingRevenueTimeSeries,
}: RevenueMonthlyAccordionsProps) => {
  const { t } = useTranslation()

  const revenueDataByMonth: Record<number, RevenueDataRow[]> = getRevenueDataByMonth(revenueData)

  if (isLoadingRevenueTimeSeries) {
    return (
      <DataGridSkeleton
        includeWrapper={true}
        numberOfColumns={SKELETON_COLUMN_SIZE}
        numberOfRows={SKELETON_ROW_SIZE}
        withToolbar={false}
      />
    )
  }

  const monthlyAccordionProps: RevenueSummaryAccordionProps[] = Object.entries(revenueDataByMonth).map(
    ([month, data]) => {
      const soldCapacity = data.reduce((total, row) => total + row.soldCapacity, 0)
      const activatedEnergy = data.reduce((total, row) => total + row.activatedEnergy, 0)
      const revenue = data.reduce((totalRevenue, row) => totalRevenue + row.revenue, 0)
      const currency = getCurrencyFromRevenueRows(data)

      return {
        title: t(`common.months.${MONTHS[month] as Month}`),
        monthlyDataGrid: <RevenueMonthlyDataGrid data={data} />,
        monthlySummary: (
          <RevenueMonthlySummary
            activatedEnergy={activatedEnergy}
            currency={currency}
            revenue={revenue}
            soldCapacity={soldCapacity}
          />
        ),
      } as RevenueSummaryAccordionProps
    },
  )

  return (
    <Box>
      {monthlyAccordionProps.map((props) => (
        <RevenueSummaryAccordion key={props.title} {...props} />
      ))}
    </Box>
  )
}
